import dayjs from 'dayjs';

import type { LanguageCodeEnum } from '@/graphql/saleor/api';
import { DEFAULT_COUNTRY } from '@/lib/regions';

import { BRAND } from './const';
import type { Brand } from './types';

const DEFAULT_CURRENCY_CODE = DEFAULT_COUNTRY.currency;
const DEFAULT_LOCALE_CODE = DEFAULT_COUNTRY.defaultLocale;

export type LocaleMessages = Record<string, string>;
export type LocaleKey = keyof LocaleMessages;

type SupportedLanguage =
  | 'DE'
  | 'EN_GB'
  | 'EN_US'
  | 'ES'
  | 'FR'
  | 'IT'
  | 'JA'
  | 'KO'
  | 'PL'
  | 'PT'
  | 'RU'
  | 'ZH'
  | 'ZH_HANT';

type MessagesMap = Record<
  SupportedLanguage,
  () => Promise<{ default: Record<string, string> }>
>;
const commonMessagesLoader: MessagesMap = {
  DE: () => import('../locale/de.json'),
  EN_GB: () => import('../locale/en-GB.json'),
  EN_US: () => import('../locale/en-US.json'),
  ES: () => import('../locale/es.json'),
  FR: () => import('../locale/fr.json'),
  IT: () => import('../locale/it.json'),
  JA: () => import('../locale/ja.json'),
  KO: () => import('../locale/ko.json'),
  PL: () => import('../locale/pl.json'),
  PT: () => import('../locale/pt.json'),
  RU: () => import('../locale/ru.json'),
  ZH: () => import('../locale/zh.json'),
  ZH_HANT: () => import('../locale/zh-Hant.json'),
};

const brandMessagesLoader: Record<Brand, MessagesMap | null> = {
  gallet: {
    DE: () => import('../locale/gallet/de.json'),
    EN_GB: () => import('../locale/gallet/en-GB.json'),
    EN_US: () => import('../locale/gallet/en-US.json'),
    ES: () => import('../locale/gallet/es.json'),
    FR: () => import('../locale/gallet/fr.json'),
    IT: () => import('../locale/gallet/it.json'),
    JA: () => import('../locale/gallet/ja.json'),
    KO: () => import('../locale/gallet/ko.json'),
    PL: () => import('../locale/gallet/pl.json'),
    PT: () => import('../locale/gallet/pt.json'),
    RU: () => import('../locale/gallet/ru.json'),
    ZH: () => import('../locale/gallet/zh.json'),
    ZH_HANT: () => import('../locale/gallet/zh-Hant.json'),
  },
  breitling: null,
};

const importMessagesByLanguageCode = async (
  messagesMap: MessagesMap | null,
  languageCode: LanguageCodeEnum,
) => {
  let messages: Record<string, string> = {};
  if (!messagesMap) return {};
  switch (languageCode) {
    case 'EN_US':
    case 'EN_GB':
    case 'ES':
    case 'DE':
    case 'FR':
    case 'IT':
    case 'JA':
    case 'KO':
    case 'PL':
    case 'PT':
    case 'RU':
      messages = (await messagesMap[languageCode]()).default;
      break;
    case 'ZH':
    case 'ZH_HANS':
      messages = (await messagesMap['ZH']()).default;
      break;
    case 'ZH_HANT':
      messages = (await messagesMap['ZH_HANT']()).default;
      break;
    default:
      messages = (await messagesMap['EN_US']()).default;
      break;
  }

  return messages;
};

export async function loadMessages(languageCode: LanguageCodeEnum) {
  const commonMessages = await importMessagesByLanguageCode(
    commonMessagesLoader,
    languageCode,
  );

  const brandMessages = await importMessagesByLanguageCode(
    brandMessagesLoader[BRAND],
    languageCode,
  );

  return {
    ...commonMessages,
    ...brandMessages,
  };
}

export const getTranslatedMessage = async (
  languageCode: LanguageCodeEnum,
  id: string,
  defaultMessage?: string,
) => {
  const messages = await loadMessages(languageCode);
  const translatedMessage =
    (messages as Record<string, string>)[id] || defaultMessage;

  return translatedMessage;
};

export const formatAsDateTime = (
  dateTime: string,
  locale = DEFAULT_LOCALE_CODE,
) =>
  new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  }).format(new Date(dateTime));

export const formatAsDate = (date: string, locale = DEFAULT_LOCALE_CODE) => {
  let time;
  switch (locale) {
    case 'en-US':
      time = dayjs(date).format('MM-DD-YYYY');
      break;
    case 'en-CA':
      time = dayjs(date).format('DD-MM-YYYY');
      break;
    case 'fr-CA':
      time = dayjs(date).format('DD-MM-YYYY');
      break;
    default:
      time = new Intl.DateTimeFormat(locale).format(new Date(date));
  }
  return time;
};
export const formatAsDateMonth = (
  date: string,
  locale = DEFAULT_LOCALE_CODE,
) => {
  let time;
  switch (locale) {
    case 'en-US':
      time = dayjs(date).format('MMMM, YYYY');
      break;
    case 'en-CA':
      time = dayjs(date).format('YYYY, MMMM');
      break;
    case 'fr-CA':
      time = dayjs(date).format('YYYY, MMMM');
      break;
    default:
      time = dayjs(date).format('YYYY, MMMM');
  }
  return time;
};

export type CURRENCY_DISPLAY = 'symbol' | 'narrowSymbol' | 'code' | 'name';

const formatPrice = (
  amount: number,
  currency: string,
  locale: string,
  opts?: {
    currencyDisplay?: CURRENCY_DISPLAY;
    maximumFractionDigits?: number;
    minimumFractionDigits?: number;
    useGrouping?: boolean;
  },
) => {
  const localeByCurrency = currency === 'USD' ? 'en-US' : locale;
  const isDecimalZero = Number(amount) % 1 === 0;
  const number = new Intl.NumberFormat(localeByCurrency, {
    style: 'currency',
    currency,
    currencyDisplay: 'code',
    maximumFractionDigits: isDecimalZero ? 0 : 2,
    minimumFractionDigits: isDecimalZero ? 0 : undefined,
    ...opts,
  });

  const parts = number.formatToParts(amount);
  const wrapped = parts.map(({ type, value }) => {
    if (type === 'minusSign') {
      return null; // We remove minus sign to add it later
    }
    return value;
  });
  const firstNumberIndex = parts.findIndex(
    (part) =>
      part.type === 'integer' ||
      part.type === 'fraction' ||
      part.type === 'decimal',
  );

  const finalWrapped =
    amount < 0
      ? [
          ...wrapped.slice(0, firstNumberIndex),
          '-',
          ...wrapped.slice(firstNumberIndex),
        ]
      : wrapped;

  return { formattedParts: finalWrapped, stringValue: finalWrapped.join('') };
};

export const formatAsPrice = (
  amount = 0,
  currency = DEFAULT_CURRENCY_CODE,
  locale = DEFAULT_LOCALE_CODE,
  opts?: {
    currencyDisplay?: CURRENCY_DISPLAY;
    maximumFractionDigits?: number;
    minimumFractionDigits?: number;
    useGrouping?: boolean;
  },
) => {
  const { formattedParts } = formatPrice(amount, currency, locale, opts);
  return <>{formattedParts}</>; // return JSX
};

export const formatAsPriceString = (
  amount = 0,
  currency = DEFAULT_CURRENCY_CODE,
  locale = DEFAULT_LOCALE_CODE,
  opts?: {
    currencyDisplay?: CURRENCY_DISPLAY;
    maximumFractionDigits?: number;
    minimumFractionDigits?: number;
    useGrouping?: boolean;
  },
) => {
  const { stringValue } = formatPrice(amount, currency, locale, opts);
  return stringValue; // return string
};
